// externals
import { RSAA } from 'redux-api-middleware';

// consts/enums
import {
    BUYER,
    CO_BUYER,
    INSURANCE,
    HIDE_EDIT_CUSTOMER_SLIDEOUT,
    SHOW_EDIT_CUSTOMER_SLIDEOUT,
    SET_CUSTOMER_TO_SYNC,
    SYNC_CUSTOMER_REQUEST,
    SYNC_CUSTOMER_SUCCESS,
    SYNC_CUSTOMER_FAILURE,
    REMOVE_CUSTOMER_FROM_SYNC,
    CREATE_BUSINESS_REQUEST,
    CREATE_BUSINESS_SUCCESS,
    CREATE_BUSINESS_FAILURE,
    CLEAR_CREATE_BUSINESS_ERROR,
    SHOW_BUSINESS_SEARCH,
    HIDE_BUSINESS_SEARCH,
    BUSINESS_SEARCH_REQUEST,
    BUSINESS_SEARCH_SUCCESS,
    BUSINESS_SEARCH_FAILURE,
    CLEAR_BUSINESS_SEARCH_RESULTS,
    SELECT_BUSINESS_RESULT,
    ASSOCIATE_BUSINESS_REQUEST,
    ASSOCIATE_BUSINESS_SUCCESS,
    ASSOCIATE_BUSINESS_FAILURE,
    CLEAR_ASSOCIATE_BUSINESS_ERROR
} from '../actionTypes/editCustomerActionTypes';

// libraries
import type { RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import { MANAGER_VIEW_APP_PREFIX, apiConfigHelper, locHrefUtil, offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { X_DR_DEALER_COMMON_ORG_ID } from '@makemydeal/dr-shared-bff-types';

// selectors
import { getCommonOrgId } from '../selectors/offerRedux';

export type CreateBusinessRequest = {
    businessName: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    suffix?: string;
    email: string;
    emailAlternative?: string;
    phone?: string;
    homePhone?: string;
    workPhone?: string;
    address?: {
        streetAddress?: string;
        streetAddress2?: string;
        city?: string;
        state?: string;
        zip?: string;
        county?: string;
    };
};

export const showEditBuyerSlideout = () => ({
    type: SHOW_EDIT_CUSTOMER_SLIDEOUT,
    payload: { showEditCustomer: BUYER }
});

export const showEditCoBuyerSlideout = () => ({
    type: SHOW_EDIT_CUSTOMER_SLIDEOUT,
    payload: { showEditCustomer: CO_BUYER }
});

export const showEditInsuranceSlideout = () => ({
    type: SHOW_EDIT_CUSTOMER_SLIDEOUT,
    payload: { showEditCustomer: INSURANCE }
});

export const hideEditCustomerSlideout = () => ({
    type: HIDE_EDIT_CUSTOMER_SLIDEOUT,
    payload: { showEditCustomer: '' }
});

export const setCustomerToSync = (commonConsumerId: string) => ({
    type: SET_CUSTOMER_TO_SYNC,
    payload: commonConsumerId
});

export const removeCustomerToSync = (commonConsumerId: string) => ({
    type: REMOVE_CUSTOMER_FROM_SYNC,
    payload: commonConsumerId
});

export const syncCustomerRSAA = (customerData: { customers: Array<Record<string, any>> }) => {
    return (dispatch: any) => {
        const locHref = locHrefUtil.getLocHref();
        const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, `sync-customer`);
        const request: RSAACallRequest = {
            endpoint: `${bffApiUrlResult.url}`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(customerData),
            types: [
                {
                    type: SYNC_CUSTOMER_REQUEST
                },
                {
                    type: SYNC_CUSTOMER_SUCCESS
                },
                {
                    type: SYNC_CUSTOMER_FAILURE
                }
            ],
            fetch: async (...args) => await fetch(...args)
        };
        dispatch({ [RSAA]: request });
    };
};

export const clearCreateBusinessError = () => ({
    type: CLEAR_CREATE_BUSINESS_ERROR
});

export const createBusinessRSAA = (requestBody: CreateBusinessRequest) => {
    return async (dispatch: any, getState: () => StateTree) => {
        const state = getState();
        const dealXgId = offerSessionUtils.getDealXgId();
        const locHref = locHrefUtil.getLocHref();
        const dealerId = String(dealerSelectors.getDealerId(state));
        const commonOrgId = getCommonOrgId(state) || '';
        const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(
            MANAGER_VIEW_APP_PREFIX,
            locHref,
            `deal/${dealXgId}/business/create`
        );

        dispatch({ type: CREATE_BUSINESS_REQUEST });

        try {
            const response = await fetch(bffApiUrlResult.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-dr-dealxgid': dealXgId,
                    'x-dr-dealerid': dealerId,
                    [X_DR_DEALER_COMMON_ORG_ID]: commonOrgId
                },
                body: JSON.stringify(requestBody)
            });

            const responseBody = await response.json().catch(() => ({}));
            const { commonConsumerId, businessEntityId } = responseBody ?? {};
            const hasRequiredIds = Boolean(commonConsumerId && businessEntityId);

            if (response.ok && responseBody?.ok && hasRequiredIds) {
                dispatch({
                    type: CREATE_BUSINESS_SUCCESS,
                    payload: responseBody
                });

                return {
                    ok: true,
                    data: responseBody
                };
            }

            const error = responseBody?.error || 'CreateBusinessFailed';
            dispatch({
                type: CREATE_BUSINESS_FAILURE,
                payload: error
            });

            return {
                ok: false,
                error
            };
        } catch (error) {
            const errorMessage = (error as Error)?.message || 'CreateBusinessFailed';
            dispatch({
                type: CREATE_BUSINESS_FAILURE,
                payload: errorMessage
            });

            return {
                ok: false,
                error: errorMessage
            };
        }
    };
};

export const showBusinessSearch = () => ({
    type: SHOW_BUSINESS_SEARCH
});

export const hideBusinessSearch = () => ({
    type: HIDE_BUSINESS_SEARCH
});

export const clearBusinessSearchResults = () => ({
    type: CLEAR_BUSINESS_SEARCH_RESULTS
});

export const searchBusinessByName = (query: string) => {
    return async (dispatch: any, getState: () => StateTree) => {
        const state = getState();
        const dealXgId = offerSessionUtils.getDealXgId();
        const locHref = locHrefUtil.getLocHref();
        const dealerId = String(dealerSelectors.getDealerId(state));
        const commonOrgId = getCommonOrgId(state) || '';
        const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(
            MANAGER_VIEW_APP_PREFIX,
            locHref,
            `deal/${dealXgId}/business/search`
        );

        dispatch({ type: BUSINESS_SEARCH_REQUEST, payload: { query } });

        try {
            const response = await fetch(bffApiUrlResult.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-dr-dealxgid': dealXgId,
                    'x-dr-dealerid': dealerId,
                    [X_DR_DEALER_COMMON_ORG_ID]: commonOrgId
                },
                body: JSON.stringify({ query })
            });

            const responseBody = await response.json().catch(() => ({}));

            if (response.ok && responseBody?.ok) {
                dispatch({
                    type: BUSINESS_SEARCH_SUCCESS,
                    payload: { results: responseBody.results || [] }
                });
            } else {
                //TODO: Handle error case in user friendly way. For now, we are dispatching a failure action with a generic error message.
                dispatch({
                    type: BUSINESS_SEARCH_FAILURE,
                    payload: { error: 'SearchFailed' }
                });
            }
        } catch (error) {
            dispatch({
                type: BUSINESS_SEARCH_FAILURE,
                payload: { error: 'SearchFailed' }
            });
        }
    };
};

export const selectBusinessResult = (businessEntityId: string) => ({
    type: SELECT_BUSINESS_RESULT,
    payload: { businessEntityId }
});

export const clearAssociateBusinessError = () => ({
    type: CLEAR_ASSOCIATE_BUSINESS_ERROR
});

export type AssociateBusinessRequest = {
    businessEntityId: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    suffix?: string;
    email?: string;
    emailAlternative?: string;
    phone?: string;
    homePhone?: string;
    workPhone?: string;
    address?: {
        streetAddress?: string;
        streetAddress2?: string;
        city?: string;
        state?: string;
        zip?: string;
        county?: string;
    };
};

export const associateBusiness = (requestBody: AssociateBusinessRequest) => {
    return async (dispatch: any, getState: () => StateTree) => {
        const state = getState();
        const dealXgId = offerSessionUtils.getDealXgId();
        const locHref = locHrefUtil.getLocHref();
        const dealerId = String(dealerSelectors.getDealerId(state));
        const commonOrgId = getCommonOrgId(state) || '';
        const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(
            MANAGER_VIEW_APP_PREFIX,
            locHref,
            `deal/${dealXgId}/business/associate`
        );

        dispatch({ type: ASSOCIATE_BUSINESS_REQUEST });

        try {
            const response = await fetch(bffApiUrlResult.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-dr-dealxgid': dealXgId,
                    'x-dr-dealerid': dealerId,
                    [X_DR_DEALER_COMMON_ORG_ID]: commonOrgId
                },
                body: JSON.stringify(requestBody)
            });

            const responseBody = await response.json().catch(() => ({}));

            if (response.ok && responseBody?.ok) {
                dispatch({
                    type: ASSOCIATE_BUSINESS_SUCCESS,
                    payload: responseBody
                });

                return { ok: true, data: responseBody };
            }

            const error = responseBody?.error || 'AssociateBusinessFailed';
            dispatch({
                type: ASSOCIATE_BUSINESS_FAILURE,
                payload: { error }
            });

            return { ok: false, error };
        } catch (error) {
            const errorMessage = (error as Error)?.message || 'AssociateBusinessFailed';
            dispatch({
                type: ASSOCIATE_BUSINESS_FAILURE,
                payload: { error: errorMessage }
            });

            return { ok: false, error: errorMessage };
        }
    };
};
